import { Navigate, Route, Routes } from "react-router-dom";
import Landing from "@/pages/Landing";
import Index from "@/pages/Index";
import Account from "@/pages/Account";
import NotFound from "@/pages/NotFound";
import { GetHelpSection } from "@/components/Account/sections/GetHelpSection";
import MyIssuesSection from "@/components/Account/sections/MyIssuesSection";
import { ReportIssueSection } from "@/components/Account/sections/ReportIssueSection";
import PrivateRoute from "@/components/PrivateRoute";
import Layout from "@/components/Layout";
import WebViewInit from "@/pages/WebViewInit";
import TermsofServices from "@/components/TermsofServices";
import DataProcessingandAgreement from "@/components/DataProcessingandAgreement";
import TermsAndCondition from "@/components/TermsAndConditions";
import CancellationPolicy from "@/components/CancellationPolicy";
import PrivacyPolicy from "@/components/PrivacyPolicy";
import DevTeam from "@/pages/devTeam";
import DeleteAccount from "@/components/DeleteAccount";
import VerificationProcess from "@/pages/VerificationProcess";
import AvailableRides from "@/pages/AvailableRides";
import { FindRidePage } from "@/features/find-ride";
import { CorporateCarpoolPage } from "@/features/corporate-carpool";
import { NewsPage } from "@/features/news";
import WaitingGuests from "@/pages/WaitingGuests";
import WhyDuri from "@/pages/WhyDuri";
import AboutUs from "@/pages/AboutUs";
import DailyCommute from "@/pages/DailyCommute";
import CommuteCalculator from "@/pages/CommuteCalculator";
import EmailVerification from "@/pages/EmailVerification";
import RideInfo from "@/pages/RideInfo";
import BookingInfo from "@/pages/BookingInfo";
import TrackRide from "@/pages/TrackRide";
import Faqs from "@/pages/Faqs";
import CorridorPage from "@/pages/CorridorPage";
import HowDuriWorks from "@/pages/HowDuriWorks";
import PopularRoutes from "@/pages/PopularRoutes";
import OfferARide from "@/pages/OfferARide";
import Safety from "@/pages/Safety";
import RidesharingImpact from "@/pages/RidesharingImpact";
import CityHubPage from "@/pages/CityHubPage";
import GuideArticlePage from "@/pages/GuideArticlePage";
import IntercityHubPage from "@/pages/IntercityHubPage";
import type { Dispatch, SetStateAction } from "react";
import { ROUTE_PATHS } from "./routePaths";
import { LegacyCorridorRedirect } from "./legacyRedirects";

type AuthMode = "login" | "register";

type AppRoutesProps = {
  showAuthModal: boolean;
  setShowAuthModal: Dispatch<SetStateAction<boolean>>;
  authMode: AuthMode;
  setAuthMode: Dispatch<SetStateAction<AuthMode>>;
  showQuickRideModal: boolean;
  setShowQuickRideModal: Dispatch<SetStateAction<boolean>>;
  selectedCountry: string;
  setSelectedCountry: Dispatch<SetStateAction<string>>;
  handleAuthClick: (mode: AuthMode) => void;
};

export default function AppRoutes({
  showAuthModal,
  setShowAuthModal,
  authMode,
  setAuthMode,
  showQuickRideModal,
  setShowQuickRideModal,
  selectedCountry,
  setSelectedCountry,
  handleAuthClick,
}: AppRoutesProps) {
  const siteChrome = (
    <Layout
      headerProps={{
        onAuthClick: handleAuthClick,
        selectedCountry,
        onCountryChange: setSelectedCountry,
      }}
      footerProps={{
        showAuthModal,
        setShowAuthModal,
        setAuthMode,
      }}
    />
  );

  return (
    <Routes>
      <Route element={siteChrome}>
        <Route
          index
          element={
            <Landing
              showAuthModal={showAuthModal}
              setShowAuthModal={setShowAuthModal}
              authMode={authMode}
              setAuthMode={setAuthMode}
              showQuickRideModal={showQuickRideModal}
              setShowQuickRideModal={setShowQuickRideModal}
              selectedCountry={selectedCountry}
              setSelectedCountry={setSelectedCountry}
            />
          }
        />
        <Route path="home" element={<Navigate to={ROUTE_PATHS.HOME} replace />} />

        {/* Canonical marketing routes */}
        <Route path={ROUTE_PATHS.HOW_DURI_WORKS.slice(1)} element={<HowDuriWorks />} />
        <Route
          path={ROUTE_PATHS.FIND_RIDE.slice(1)}
          element={
            <FindRidePage
              showAuthModal={showAuthModal}
              setShowAuthModal={setShowAuthModal}
              setAuthMode={setAuthMode}
            />
          }
        />
        <Route path={ROUTE_PATHS.FIND_RIDES.slice(1)} element={<AvailableRides />} />
        <Route path={ROUTE_PATHS.OFFER_A_RIDE.slice(1)} element={<OfferARide />} />
        <Route path={ROUTE_PATHS.RIDE_REQUESTS.slice(1)} element={<WaitingGuests />} />
        <Route path={ROUTE_PATHS.DAILY_COMMUTE_CARPOOL.slice(1)} element={<DailyCommute />} />
        <Route
          path={ROUTE_PATHS.CORPORATE_CARPOOL.slice(1)}
          element={<CorporateCarpoolPage />}
        />
        <Route
          path="corporate-carpool"
          element={<Navigate to={ROUTE_PATHS.CORPORATE_CARPOOL} replace />}
        />
        <Route
          path={ROUTE_PATHS.COMMUTE_CALCULATOR.slice(1)}
          element={<CommuteCalculator />}
        />
        <Route
          path={ROUTE_PATHS.EMAIL_VERIFICATION.slice(1)}
          element={<EmailVerification />}
        />
        <Route
          path={ROUTE_PATHS.RIDE_INFO.slice(1)}
          element={<RideInfo />}
        />
        <Route
          path={ROUTE_PATHS.BOOKING_INFO.slice(1)}
          element={<BookingInfo />}
        />
        <Route
          path={ROUTE_PATHS.TRACK_RIDE.slice(1)}
          element={<TrackRide />}
        />
        <Route path={ROUTE_PATHS.HYDERABAD_DAILY_COMMUTE_CARPOOL.slice(1)} element={<DailyCommute />} />
        <Route path={ROUTE_PATHS.VIJAYAWADA_DAILY_COMMUTE_CARPOOL.slice(1)} element={<DailyCommute />} />
        <Route path={ROUTE_PATHS.INTERCITY_RIDESHARING_INDIA.slice(1)} element={<IntercityHubPage />} />
        <Route path={ROUTE_PATHS.HYDERABAD_CARPOOL.slice(1)} element={<CityHubPage />} />
        <Route path={ROUTE_PATHS.VIJAYAWADA_RIDESHARING.slice(1)} element={<CityHubPage />} />
        <Route path={ROUTE_PATHS.BANGALORE_CARPOOL.slice(1)} element={<CityHubPage />} />
        <Route path={ROUTE_PATHS.CHENNAI_RIDESHARING.slice(1)} element={<CityHubPage />} />
        <Route path={`${ROUTE_PATHS.GUIDES.slice(1)}/:slug`} element={<GuideArticlePage />} />
        <Route path={ROUTE_PATHS.POPULAR_ROUTES.slice(1)} element={<PopularRoutes />} />
        <Route path={ROUTE_PATHS.SAFETY.slice(1)} element={<Safety />} />
        <Route path={ROUTE_PATHS.ABOUT_DURI.slice(1)} element={<AboutUs />} />
        <Route path={ROUTE_PATHS.NEWS.slice(1)} element={<NewsPage />} />
        <Route path={ROUTE_PATHS.RIDESHARING_BENEFITS.slice(1)} element={<WhyDuri />} />
        <Route path={ROUTE_PATHS.RIDESHARING_IMPACT.slice(1)} element={<RidesharingImpact />} />
        <Route path={ROUTE_PATHS.HELP_CENTER.slice(1)} element={<GetHelpSection />} />
        <Route path={ROUTE_PATHS.FAQS.slice(1)} element={<Faqs />} />
        <Route path={`${ROUTE_PATHS.INTERCITY_ROUTES.slice(1)}/:slug`} element={<CorridorPage />} />

        {/* Legacy URL redirects (SEO preservation) */}
        <Route path="find-rides" element={<Navigate to={ROUTE_PATHS.FIND_RIDES} replace />} />
        <Route path="waiting-guests" element={<Navigate to={ROUTE_PATHS.RIDE_REQUESTS} replace />} />
        <Route path="available-guests" element={<Navigate to={ROUTE_PATHS.RIDE_REQUESTS} replace />} />
        <Route path="daily-commute" element={<Navigate to={ROUTE_PATHS.DAILY_COMMUTE_CARPOOL} replace />} />
        <Route path="daily-commutes" element={<Navigate to={ROUTE_PATHS.DAILY_COMMUTE_CARPOOL} replace />} />
        <Route path="why-duri" element={<Navigate to={ROUTE_PATHS.RIDESHARING_BENEFITS} replace />} />
        <Route path="about-us" element={<Navigate to={ROUTE_PATHS.ABOUT_DURI} replace />} />
        <Route path="safety-impact" element={<Navigate to={ROUTE_PATHS.SAFETY} replace />} />
        <Route path="getHelp" element={<Navigate to={ROUTE_PATHS.HELP_CENTER} replace />} />
        <Route path="corridor/:slug" element={<LegacyCorridorRedirect />} />
        <Route path="route/:slug" element={<LegacyCorridorRedirect />} />

        <Route path={ROUTE_PATHS.TERMS_OF_SERVICES.slice(1)} element={<TermsofServices />} />
        <Route path={ROUTE_PATHS.CANCELLATION_POLICY.slice(1)} element={<CancellationPolicy />} />
        <Route path={ROUTE_PATHS.TERMS_AND_CONDITIONS.slice(1)} element={<TermsAndCondition />} />
        <Route
          path={ROUTE_PATHS.DATA_PROCESSING_AND_AGREEMENT.slice(1)}
          element={<DataProcessingandAgreement />}
        />
        <Route path={ROUTE_PATHS.PRIVACY_POLICY.slice(1)} element={<PrivacyPolicy />} />
        <Route path={ROUTE_PATHS.DEV_TEAM.slice(1)} element={<DevTeam />} />
        <Route path={ROUTE_PATHS.VERIFICATION_PROCESS.slice(1)} element={<VerificationProcess />} />

        {/* Private routes — same site header/footer as public pages */}
        <Route
          element={
            <PrivateRoute
              setShowAuthModal={setShowAuthModal}
              setAuthMode={setAuthMode}
            />
          }
        >
          <Route path={ROUTE_PATHS.DASHBOARD.slice(1)} element={<Index />} />
          <Route path={ROUTE_PATHS.DELETE_ACCOUNT.slice(1)} element={<DeleteAccount />} />
          <Route path={ROUTE_PATHS.ACCOUNT.slice(1)} element={<Account />}>
            <Route path="GetHelp" element={<GetHelpSection />} />
            <Route path="MyIssues" element={<MyIssuesSection />} />
            <Route path="ReportAnIssue" element={<ReportIssueSection />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>

      <Route path={ROUTE_PATHS.WEBVIEW_INIT} element={<WebViewInit />} />
    </Routes>
  );
}
